import { supabase } from '../../../shared/lib/supabaseClient';
import { logActivity } from '../../../shared/services/activityLogService';
import { describeFieldChanges, describeRecordDetails } from '../../../shared/utils/describeChanges';

const STUDENT_FIELD_LABELS = {
  firstname: 'firstname',
  lastname: 'lastname',
  middlename: 'middle name',
  lrn: 'LRN',
  school_year: 'school year',
  grade_level: 'grade level',
  section: 'section',
  strand: 'strand',
  guardian_name: 'guardian name',
  guardian_contact: 'guardian contact',
  province: 'province',
  town: 'town',
  brgy: 'barangay',
  status: 'status',
};

const FETCH_PAGE_SIZE = 1000;

export async function getStudents() {
  const allRows = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from('students')
      .select('*')
      .order('lastname', { ascending: true })
      .range(from, from + FETCH_PAGE_SIZE - 1);

    if (error) throw error;
    allRows.push(...data);

    if (data.length < FETCH_PAGE_SIZE) break;
    from += FETCH_PAGE_SIZE;
  }

  return allRows;
}

export async function createStudent(studentData) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;

  const { error } = await supabase.from('students').insert({
    ...studentData,
    created_by: user.id,
  });
  if (error) throw error;
  await logActivity('add_student', `Added student ${studentData.firstname} ${studentData.lastname}`);
}

export async function bulkCreateStudents(rows) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;

  const { error } = await supabase.from('students').insert(
    rows.map((row) => ({ ...row, created_by: user.id }))
  );
  if (error) throw error;
  await logActivity('add_student', `Imported ${rows.length} students`);
}

export async function updateStudent(id, studentData, previousData) {
  const { error } = await supabase.from('students').update(studentData).eq('id', id);
  if (error) throw error;

  const displayName = `${studentData.firstname} ${studentData.lastname}`;
  const changes = previousData
    ? describeFieldChanges({
        before: previousData,
        after: studentData,
        fields: STUDENT_FIELD_LABELS,
      })
    : [];
  const description = changes.length
    ? `Updated ${changes.join(', ')} for ${displayName}`
    : `Updated student ${displayName}`;
  await logActivity('update_student', description);
}

export async function deleteStudent(id, record) {
  const { error } = await supabase.from('students').delete().eq('id', id);
  if (error) throw error;

  const displayName = record ? `${record.firstname} ${record.lastname}` : id;
  const details = describeRecordDetails({
    record,
    fields: STUDENT_FIELD_LABELS,
    exclude: ['firstname', 'lastname'],
  });
  const description = details.length
    ? `Deleted student ${displayName} (${details.join(', ')})`
    : `Deleted student ${displayName}`;
  await logActivity('delete_student', description);
}
